import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent } from "@/components/ui/chart";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, AreaChart, Area, Legend, BarChart, Bar, PieChart, Pie, Cell } from "recharts";
import { Clock, Server, Activity, HardDrive, Wifi, Thermometer, Shield, Settings, AlertTriangle, Database, Cpu, MemoryStick, Globe, Lock, Info } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { format } from "date-fns";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useSearchParams } from "react-router-dom";
import { EventDetailsDialog } from "@/components/EventDetailsDialog";
import { useEventAggregation } from "@/hooks/useEventAggregation";


interface MetricsData {
  id: string;
  hostname?: string;
  timestamp: string;
  
  // 1. Core System Performance
  cpu_usage?: number;
  cpu_cores_usage?: any;
  cpu_load_1m?: number;
  cpu_load_5m?: number;
  cpu_load_15m?: number;
  cpu_queue_length?: number;
  cpu_context_switches_per_sec?: number;
  
  memory_usage?: number;
  memory_total_bytes?: number;
  memory_used_bytes?: number;
  memory_free_bytes?: number;
  memory_available_percent?: number;
  memory_committed_percent?: number;
  memory_page_file_usage_percent?: number;
  swap_usage_bytes?: number;
  page_faults_per_sec?: number;
  
  disk_usage?: number;
  disk_free_space_percent?: number;
  disk_read_iops?: number;
  disk_write_iops?: number;
  disk_read_latency_ms?: number;
  disk_write_latency_ms?: number;
  disk_queue_length?: number;
  disk_io_latency?: number;
  disk_iops?: number;
  disk_volumes?: any;
  disk_health?: any;
  
  network_in_mbps?: number;
  network_out_mbps?: number;
  network_bytes_in?: number;
  network_bytes_out?: number;
  network_packets_per_sec?: number;
  network_errors_per_sec?: number;
  network_drops_per_sec?: number;
  tcp_established_connections?: number;
  tcp_time_wait_connections?: number;
  network_adapters?: any;
  
  uptime_seconds?: number;
  boot_time?: string;
  boot_time_seconds?: number;
  last_boot_time?: string;
  
  // 2. Processes & Services
  process_count?: number;
  thread_count?: number;
  top_processes_cpu?: any;
  top_processes_memory?: any;
  suspicious_processes?: any;
  windows_services?: any;
  handles_count?: number;
  threads_count_selected?: number;
  
  // 3. Hardware Health
  temperature_celsius?: number;
  cpu_temp_celsius?: number;
  gpu_temp_celsius?: number;
  fan_speed_rpm?: number;
  battery_charge_percent?: number;
  battery_cycle_count?: number;
  battery_status?: string;
  disk_smart_health?: string;
  disk_reallocated_sectors?: number;
  disk_pending_sectors?: number;
  
  // 4. OS & Configuration
  pending_updates_count?: number;
  last_update_time?: string;
  bitlocker_status?: any;
  installed_roles?: any;
  installed_software?: any;
  time_zone?: string;
  
  // 5. Event Logs (aggregated)
  login_success_count?: number;
  login_failed_count?: number;
  account_locked_count?: number;
  service_crash_count?: number;
  application_crash_count?: number;
  powershell_execution_count?: number;
  event_logs_summary?: any;
  
  // New event counts structure from PowerShell
  event_counts?: any;
  event_counts_totals?: any;
  
  // 6. Network & Exposure
  listening_ports?: any;
  rdp_enabled?: boolean;
  winrm_enabled?: boolean;
  smbv1_enabled?: boolean;
  domain_joined?: boolean;
  domain_name?: string;
  dns_servers?: any;
  default_gateway?: string;
  network_shares?: any;
  open_sessions?: number;
  ntp_server?: string;
  
  // 7. Inventory & Asset
  device_serial?: string;
  bios_version?: string;
  os_edition?: string;
  windows_license_status?: string;
  hypervisor_info?: any;
  vm_generation?: string;
  vm_type?: string;
  
  // 8. Security Metrics
  firewall_enabled?: boolean;
  windows_defender_enabled?: boolean;
  windows_defender_updated?: boolean;
  antivirus_product?: string;
  antivirus_updated?: boolean;
  pending_security_updates?: number;
  admin_accounts_count?: number;
  disabled_accounts_count?: number;
  password_policy?: any;
  failed_login_last_24h?: number;
  privileged_users?: any;
  scheduled_tasks?: any;
  startup_programs?: any;
  tpm_enabled?: boolean;
  secure_boot_enabled?: boolean;
  
  // 9. Certificates & Backup
  certificates_expiring?: any;
  last_backup_time?: string;
  backup_status?: string;
  
  // Legacy metrics for compatibility
  load_average?: number;
  ping_latency_ms?: number;
  response_time_ms?: number;
  service_availability?: number;
}

interface Server {
  id: string;
  hostname: string;
  ip_address: string;
  status: string;
  last_seen: string;
  online: boolean;
}

const timeRanges = [
  { value: "15m", label: "15 minuten", minutes: 15 },
  { value: "1h", label: "1 uur", minutes: 60 },
  { value: "6h", label: "6 uur", minutes: 360 },
  { value: "24h", label: "24 uur", minutes: 1440 },
  { value: "7d", label: "7 dagen", minutes: 10080 },
];

const chartConfig = {
  cpu: { label: "CPU Usage", color: "#22c55e" },
  memory: { label: "Memory Usage", color: "#f59e0b" },
  disk: { label: "Disk Usage", color: "#06b6d4" },
  networkIn: { label: "Network In", color: "#8b5cf6" },
  networkOut: { label: "Network Out", color: "#ef4444" },
  ping: { label: "Ping Latency", color: "#3b82f6" },
  temperature: { label: "Temperature", color: "#ef4444" },
  processes: { label: "Processes", color: "#10b981" },
  services: { label: "Services", color: "#6366f1" },
  events: { label: "Events", color: "#f59e0b" },
  security: { label: "Security", color: "#dc2626" },
  hardware: { label: "Hardware", color: "#7c3aed" },
};

// Safe JSON parser that accepts strings or objects (and returns plain strings as-is)
function parseJSON<T = any>(value: any, fallback: T): T {
  if (value == null) return fallback;
  try {
    if (typeof value === 'string') {
      const s = value.trim();
      if (!s) return fallback;
      const looksJson = s.startsWith('{') || s.startsWith('[') || s.startsWith('"') || s === 'true' || s === 'false' || /^-?\d+(\.\d+)?$/.test(s);
      if (looksJson) {
        try { return JSON.parse(s) as T; } catch { return (value as unknown) as T; }
      }
      // Not JSON-looking: return the raw string so UI can show it
      return (value as unknown) as T;
    }
    if (typeof value === 'object') return value as T;
    return fallback;
  } catch (e) {
    console.warn('Failed to parse JSON field:', e);
    // If it was a string, surface it instead of hiding behind fallback
    return (typeof value === 'string' ? (value as unknown as T) : fallback);
  }
}

// Ensure we always get an array, even if the value is a single object
function toArray<T = any>(value: any): T[] {
  if (!value) return [];
  const parsed = parseJSON(value, []);
  if (Array.isArray(parsed)) return parsed;
  if (typeof parsed === 'object') return [parsed];
  return [];
}

// Normalize listening_ports field into a consistent array
function toPortsArray(value: any): any[] {
  try {
    if (!value) return [];
    let v = value;
    if (typeof v === 'string') v = parseJSON(v, null as any);
    if (Array.isArray(v)) return v as any[];
    if (v && typeof v === 'object') return Object.values(v);
    return [];
  } catch {
    return [];
  }
}

export function MetricsDashboard() {
  const { profile, loading: authLoading } = useAuth();
  
  // Add loading check for authentication
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
          <p className="text-muted-foreground">No profile found. Please refresh the page.</p>
        </div>
      </div>
    );
  }
  const [servers, setServers] = useState<any[]>([]);
  const [selectedServer, setSelectedServer] = useState<string>("");
  
  const [metricsData, setMetricsData] = useState<MetricsData[]>([]);
  const [loading, setLoading] = useState(false);
  const [serverInventory, setServerInventory] = useState<any>(null);
  const [enabledMetrics, setEnabledMetrics] = useState({
    cpu: true,
    memory: true,
    disk: true,
    networkIn: false,
    networkOut: false,
    ping: false,
    temperature: false,
    processes: false,
    services: false,
    events: false,
    security: false,
    hardware: false,
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "performance");
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [selectedEventType, setSelectedEventType] = useState<'login_success' | 'login_failure' | 'account_locked' | 'service_crash' | 'app_crash' | 'powershell' | null>(null);
  
  // Per-tab time ranges
  const [performanceTimeRange, setPerformanceTimeRange] = useState("1h");
  const [processesTimeRange, setProcessesTimeRange] = useState("1h");
  const [securityTimeRange, setSecurityTimeRange] = useState("24h");
  const [osTimeRange, setOsTimeRange] = useState("24h");
  const [eventsTimeRange, setEventsTimeRange] = useState("24h");
  const [networkTimeRange, setNetworkTimeRange] = useState("1h");
  const [inventoryTimeRange, setInventoryTimeRange] = useState("24h");
  
  // Update URL when tab changes
  useEffect(() => {
    setSearchParams({ tab: activeTab }, { replace: true });
  }, [activeTab, setSearchParams]);

  // Fetch servers with online status
  useEffect(() => {
    if (!profile?.tenant_id) return;

    const fetchServers = async () => {
      const { data } = await supabase
        .from('v_server_status')
        .select('id, hostname, ip_address, status, last_seen, online')
        .eq('tenant_id', profile.tenant_id)
        .order('hostname');
      
      if (data) {
        const typedData = data.map(server => ({
          ...server,
          ip_address: String(server.ip_address)
        }));
        setServers(typedData);
        if (typedData.length > 0 && !selectedServer) {
          setSelectedServer(typedData[0].hostname);
        }
      }
    };

    fetchServers();
    
    // Poll every 30 seconds to keep status updated
    const interval = setInterval(fetchServers, 30000);
    return () => clearInterval(interval);
  }, [profile?.tenant_id, selectedServer]);

  // Fetch server inventory data from latest metrics
  useEffect(() => {
    if (!profile?.tenant_id || !selectedServer) return;

    const fetchServerInventory = async () => {
      const { data } = await supabase
        .from('metrics_history')
        .select(`
          device_serial, bios_version, os_edition, vm_type, vm_generation,
          hypervisor_info, installed_software, installed_roles, time_zone,
          windows_license_status, bitlocker_status, pending_updates_count,
          last_update_time, rdp_enabled, winrm_enabled, smbv1_enabled,
          listening_ports, windows_services, handles_count, disk_smart_health
        `)
        .eq('tenant_id', profile.tenant_id)
        .eq('hostname', selectedServer)
        .order('timestamp', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      if (data) {
        setServerInventory(data);
      }
    };

    fetchServerInventory();
  }, [profile?.tenant_id, selectedServer]);

  // Get the current time range based on active tab
  const getCurrentTimeRange = () => {
    switch (activeTab) {
      case 'performance': return performanceTimeRange;
      case 'processes': return processesTimeRange;
      case 'security': return securityTimeRange;
      case 'os': return osTimeRange;
      case 'events': return eventsTimeRange;
      case 'network': return networkTimeRange;
      case 'inventory': return inventoryTimeRange;
      default: return '1h';
    }
  };

  // Fetch metrics data
  useEffect(() => {
    if (!profile?.tenant_id || !selectedServer) return;

    const fetchMetrics = async () => {
      setLoading(true);
      const currentRange = getCurrentTimeRange();
      const range = timeRanges.find(r => r.value === currentRange);
      const startTime = new Date(Date.now() - (range?.minutes || 60) * 60 * 1000);

      const { data } = await supabase
        .from('metrics_history')
        .select(`
          id, timestamp, hostname, ip_address,
          cpu_usage, memory_usage, disk_usage, uptime_seconds,
          cpu_cores_usage, cpu_queue_length, cpu_context_switches_per_sec,
          memory_available_percent, memory_committed_percent, memory_page_file_usage_percent,
          disk_io_latency, disk_read_latency_ms, disk_write_latency_ms, disk_iops, disk_free_space_percent, disk_queue_length,
          network_in_mbps, network_out_mbps, network_packets_per_sec, network_errors_per_sec, network_drops,
          tcp_established_connections, tcp_time_wait_connections,
          process_count, thread_count, handles_count, load_average,
          ping_latency_ms, temperature_celsius, fan_speed_rpm,
          response_time_ms, service_availability,
          battery_charge_percent, battery_status, disk_smart_health,
          pending_updates_count, last_update_time, bitlocker_status, windows_license_status,
          listening_ports, rdp_enabled, winrm_enabled, smbv1_enabled,
          device_serial, bios_version, os_edition, vm_type, hypervisor_info,
          installed_software, installed_roles, windows_services,
          login_success_count, login_failed_count, account_locked_count, service_crash_count, application_crash_count, powershell_execution_count,
          event_counts, event_counts_totals,
          firewall_enabled, windows_defender_enabled, windows_defender_updated,
          antivirus_product, antivirus_updated, pending_security_updates,
          admin_accounts_count, disabled_accounts_count, password_policy,
          failed_login_last_24h, privileged_users, scheduled_tasks, startup_programs,
          tpm_enabled, secure_boot_enabled, certificates_expiring,
          last_backup_time, backup_status, boot_time_seconds,
          domain_joined, domain_name, dns_servers, default_gateway,
          network_shares, open_sessions, ntp_server, network_adapters,
          disk_volumes, disk_health, top_processes_cpu, top_processes_memory,
          vm_generation, time_zone, last_boot_time
        `)
        .eq('tenant_id', profile.tenant_id)
        .eq('hostname', selectedServer)
        .gte('timestamp', startTime.toISOString())
        .order('timestamp', { ascending: true });

      if (data) {
        setMetricsData(data);
      }
      setLoading(false);
    };

    fetchMetrics();
  }, [profile?.tenant_id, selectedServer, performanceTimeRange, processesTimeRange, securityTimeRange, osTimeRange, eventsTimeRange, networkTimeRange, inventoryTimeRange, activeTab]);

  // Real-time updates for metrics
  useEffect(() => {
    if (!profile?.tenant_id || !selectedServer) return;

    const channel = supabase
      .channel('metrics-updates')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'metrics_history',
          filter: `tenant_id=eq.${profile.tenant_id}`,
        },
        (payload) => {
          const newMetrics = payload.new as MetricsData;
          if (newMetrics.hostname === selectedServer) {
            setMetricsData(prev => {
              const updated = [...prev, newMetrics];
              // Keep only recent data based on current tab's time range
              const currentRange = getCurrentTimeRange();
              const range = timeRanges.find(r => r.value === currentRange);
              const cutoff = new Date(Date.now() - (range?.minutes || 60) * 60 * 1000);
              return updated.filter(m => new Date(m.timestamp) >= cutoff);
            });
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'servers',
          filter: `tenant_id=eq.${profile.tenant_id}`,
        },
        (payload) => {
          // Update server status when last_seen changes
          setServers(prev => 
            prev.map(s => 
              s.id === payload.new.id 
                ? { 
                    ...s, 
                    last_seen: payload.new.last_seen as string,
                    online: (Date.now() - new Date(payload.new.last_seen as string).getTime()) <= 90000
                  }
                : s
            )
          );
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [profile?.tenant_id, selectedServer, activeTab, performanceTimeRange, processesTimeRange, securityTimeRange, osTimeRange, eventsTimeRange, networkTimeRange, inventoryTimeRange]);

  // Prepare chart data
  const chartData = useMemo(() => {
    return metricsData.map(metric => ({
      timestamp: format(new Date(metric.timestamp), 'HH:mm'),
      fullTimestamp: metric.timestamp,
      
      // Core metrics
      cpu: metric.cpu_usage || 0,
      memory: metric.memory_usage || 0,
      disk: metric.disk_usage || 0,
      networkIn: metric.network_in_mbps || 0,
      networkOut: metric.network_out_mbps || 0,
      
      // CPU details
      cpuLoad1m: metric.cpu_load_1m || 0,
      cpuLoad5m: metric.cpu_load_5m || 0,
      cpuLoad15m: metric.cpu_load_15m || 0,
      cpuQueueLength: metric.cpu_queue_length || 0,
      contextSwitches: metric.cpu_context_switches_per_sec || 0,
      
      // Memory details
      memoryAvailable: metric.memory_available_percent || 0,
      memoryCommitted: metric.memory_committed_percent || 0,
      pageFileUsage: metric.memory_page_file_usage_percent || 0,
      swapUsage: metric.swap_usage_bytes || 0,
      
      // Disk details
      diskReadLatency: metric.disk_read_latency_ms || 0,
      diskWriteLatency: metric.disk_write_latency_ms || 0,
      diskReadIOPS: metric.disk_read_iops || 0,
      diskWriteIOPS: metric.disk_write_iops || 0,
      diskQueueLength: metric.disk_queue_length || 0,
      diskFreeSpace: metric.disk_free_space_percent || 0,
      
      // Network details
      networkPackets: metric.network_packets_per_sec || 0,
      networkErrors: metric.network_errors_per_sec || 0,
      networkDrops: metric.network_drops_per_sec || 0,
      tcpEstablished: metric.tcp_established_connections || 0,
      tcpTimeWait: metric.tcp_time_wait_connections || 0,
      
      // System details
      uptime: metric.uptime_seconds || 0,
      processCount: metric.process_count || 0,
      threadCount: metric.thread_count || 0,
      handlesCount: metric.handles_count || 0,
      
      // Hardware health
      temperature: metric.temperature_celsius || 0,
      cpuTemp: metric.cpu_temp_celsius || 0,
      gpuTemp: metric.gpu_temp_celsius || 0,
      fanSpeed: metric.fan_speed_rpm || 0,
      batteryCharge: metric.battery_charge_percent || 0,
      
      // Event logs
      loginSuccess: metric.login_success_count || 0,
      loginFailed: metric.login_failed_count || 0,
      accountLocked: metric.account_locked_count || 0,
      serviceCrash: metric.service_crash_count || 0,
      appCrash: metric.application_crash_count || 0,
      powershellExec: metric.powershell_execution_count || 0,
      
      // Legacy
      ping: metric.ping_latency_ms || 0,
      responseTime: metric.response_time_ms || 0,
      availability: metric.service_availability || 0,
      loadAverage: metric.load_average || 0,
      diskIO: metric.disk_io_latency || 0,
      diskIOPS: metric.disk_iops || 0,
    }));
  }, [metricsData]);

  const currentServer = servers.find(s => s.hostname === selectedServer);
  const latestMetrics = metricsData[metricsData.length - 1];

  // Helper: find most recent row where at least one of the given fields is not null/undefined
  const findLatestWithFields = (fields: (keyof MetricsData)[]) => {
    const rows = [...metricsData].reverse();
    return rows.find((m) => fields.some((f) => m[f] != null)) || latestMetrics;
  };

  // Use the most recent row that actually contains security fields
  const latestSecurityMetrics = useMemo(() => {
    return findLatestWithFields([
      'firewall_enabled', 'windows_defender_enabled', 'windows_defender_updated',
      'tpm_enabled', 'secure_boot_enabled', 'pending_security_updates',
      'admin_accounts_count', 'antivirus_product', 'failed_login_last_24h'
    ]);
  }, [metricsData, latestMetrics]);

  // Use most recent row with inventory/machine fields
  const latestInventoryMetrics = useMemo(() => {
    return findLatestWithFields([
      'device_serial', 'bios_version', 'os_edition', 'vm_type', 'vm_generation', 'hypervisor_info'
    ]);
  }, [metricsData, latestMetrics]);

  // Use most recent row with domain/network info
  const latestNetworkMetrics = useMemo(() => {
    return findLatestWithFields([
      'domain_joined', 'domain_name', 'default_gateway', 'ntp_server', 'dns_servers'
    ]);
  }, [metricsData, latestMetrics]);

  const effectiveStatus = currentServer
    ? (currentServer.online ? 'online' : (['warning','critical'].includes(currentServer.status) ? currentServer.status : 'offline'))
    : 'offline';

  // Compute event totals using Edge Function (source of truth for time ranges)
  const { eventTotals, loading: eventsLoading, error: eventsError } = useEventAggregation(
    selectedServer,
    eventsTimeRange
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Windows Agent Metrics Dashboard</h1>
          <p className="text-muted-foreground">
            Comprehensive Windows monitoring with all 7 metric categories
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.location.reload()}
            className="flex items-center gap-2"
          >
            <Activity className="h-4 w-4" />
            Refresh
          </Button>
          <Select value={selectedServer} onValueChange={setSelectedServer}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Selecteer server" />
            </SelectTrigger>
            <SelectContent>
              {servers.map((server) => (
                <SelectItem key={server.id} value={server.hostname}>
                  <div className="flex items-center gap-2">
                    <Server className="h-4 w-4" />
                    {server.hostname}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

        </div>
      </div>

      {/* Server Status */}
      {currentServer && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Server className="h-5 w-5" />
              {currentServer.hostname}
            </CardTitle>
            <CardDescription>
              {currentServer.ip_address} • 
              <Badge 
                variant={
                  effectiveStatus === 'online' ? 'default' :
                  effectiveStatus === 'warning' ? 'default' :
                  effectiveStatus === 'critical' ? 'destructive' : 'secondary'
                }
                className="ml-2"
              >
                {effectiveStatus}
              </Badge>
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      {/* Comprehensive Tabbed Interface */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-7">
          <TabsTrigger value="performance" className="flex items-center gap-1">
            <Cpu className="h-4 w-4" />
            Performance
          </TabsTrigger>
          <TabsTrigger value="processes" className="flex items-center gap-1">
            <Activity className="h-4 w-4" />
            Processes
          </TabsTrigger>
          <TabsTrigger value="security" className="flex items-center gap-1">
            <Shield className="h-4 w-4" />
            Security
          </TabsTrigger>
          <TabsTrigger value="os" className="flex items-center gap-1">
            <Settings className="h-4 w-4" />
            OS Config
          </TabsTrigger>
          <TabsTrigger value="events" className="flex items-center gap-1">
            <AlertTriangle className="h-4 w-4" />
            Events
          </TabsTrigger>
          <TabsTrigger value="network" className="flex items-center gap-1">
            <Globe className="h-4 w-4" />
            Network
          </TabsTrigger>
          <TabsTrigger value="inventory" className="flex items-center gap-1">
            <Database className="h-4 w-4" />
            Inventory
          </TabsTrigger>
        </TabsList>

        {/* Performance Tab */}
        <TabsContent value="performance" className="space-y-6">
          {/* Time Range Selector */}
          <div className="flex justify-end">
            <Select value={performanceTimeRange} onValueChange={setPerformanceTimeRange}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Select time range" />
              </SelectTrigger>
              <SelectContent>
                {timeRanges.map((range) => (
                  <SelectItem key={range.value} value={range.value}>
                    {range.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {latestMetrics && (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-6">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">CPU Usage</CardTitle>
                  <Cpu className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{latestMetrics.cpu_usage?.toFixed(1) || '0.0'}%</div>
                  <p className="text-xs text-muted-foreground">
                    Load 1m: {((latestMetrics.cpu_load_1m ?? latestMetrics.load_average) !== null && (latestMetrics.cpu_load_1m ?? latestMetrics.load_average) !== undefined) ? ((latestMetrics.cpu_load_1m ?? latestMetrics.load_average) as number).toFixed(2) : 'N/A'}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Memory</CardTitle>
                  <MemoryStick className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{latestMetrics.memory_usage?.toFixed(1) || '0.0'}%</div>
                  <p className="text-xs text-muted-foreground">
                    Available: {latestMetrics.memory_available_percent?.toFixed(1) || 'N/A'}%
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Disk</CardTitle>
                  <HardDrive className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{latestMetrics.disk_usage?.toFixed(1) || '0.0'}%</div>
                  <p className="text-xs text-muted-foreground">
                    Free: {latestMetrics.disk_free_space_percent?.toFixed(1) || 
                           (latestMetrics.disk_usage ? (100 - latestMetrics.disk_usage).toFixed(1) : 'N/A')}%
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Network</CardTitle>
                  <Wifi className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{latestMetrics.network_in_mbps?.toFixed(1) || '0.0'} Mbps</div>
                  <p className="text-xs text-muted-foreground">
                    TCP Conn: {latestMetrics.tcp_established_connections ?? 'N/A'}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Uptime</CardTitle>
                  <Clock className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {latestMetrics.uptime_seconds ? 
                      Math.floor(latestMetrics.uptime_seconds / 86400) + 'd' : '0d'}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {latestMetrics.uptime_seconds ? 
                      (() => {
                        const bootTime = new Date(Date.now() - (latestMetrics.uptime_seconds * 1000));
                        return format(bootTime, 'dd/MM HH:mm');
                      })() : 'Unknown'}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Load Average</CardTitle>
                  <Activity className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {latestMetrics.load_average && latestMetrics.load_average > 0 ? 
                      latestMetrics.load_average.toFixed(2) : 'N/A'}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {latestMetrics.load_average && latestMetrics.load_average > 0 ?
                      `5m: ${latestMetrics.cpu_load_5m?.toFixed(2) || 'N/A'} | 15m: ${latestMetrics.cpu_load_15m?.toFixed(2) || 'N/A'}` :
                      'Not available on Windows'}
                  </p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Core Performance Charts */}
          <div className="grid gap-6">
        {/* Core Metrics Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Core System Metrics</CardTitle>
            <CardDescription>CPU, Memory, and Disk Usage over time</CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-[400px]">
              <LineChart data={chartData}>
                <CartesianGrid 
                  strokeDasharray="none" 
                  stroke="rgba(255,255,255,0.1)" 
                  horizontal={true}
                  vertical={true}
                />
                <XAxis 
                  dataKey="timestamp" 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: 'var(--muted-foreground)' }}
                />
                <YAxis 
                  domain={[0, 100]} 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: 'var(--muted-foreground)' }}
                  tickFormatter={(value) => `${value}%`}
                />
                <ChartTooltip
                  content={<ChartTooltipContent />}
                  labelFormatter={(value, payload) => {
                    if (payload && payload[0]) {
                      return format(new Date(payload[0].payload.fullTimestamp), 'dd/MM HH:mm');
                    }
                    return value;
                  }}
                />
                <Legend 
                  verticalAlign="bottom" 
                  height={36}
                  iconType="line"
                  wrapperStyle={{ 
                    paddingTop: '20px',
                    fontSize: '14px',
                    color: 'var(--muted-foreground)'
                  }}
                />
                {enabledMetrics.cpu && (
                  <Line
                    type="monotone"
                    dataKey="cpu"
                    stroke={chartConfig.cpu.color}
                    strokeWidth={2}
                    dot={false}
                    name="CPU Usage"
                  />
                )}
                {enabledMetrics.memory && (
                  <Line
                    type="monotone"
                    dataKey="memory"
                    stroke={chartConfig.memory.color}
                    strokeWidth={2}
                    dot={false}
                    name="Memory Usage"
                  />
                )}
                {enabledMetrics.disk && (
                  <Line
                    type="monotone"
                    dataKey="disk"
                    stroke={chartConfig.disk.color}
                    strokeWidth={2}
                    dot={false}
                    name="Disk Usage"
                  />
                )}
              </LineChart>
            </ChartContainer>
          </CardContent>
        </Card>

        {/* Network Chart */}
        {(enabledMetrics.networkIn || enabledMetrics.networkOut) && (
          <Card>
            <CardHeader>
              <CardTitle>Network Throughput</CardTitle>
              <CardDescription>Network traffic in/out (Mbps)</CardDescription>
            </CardHeader>
            <CardContent>
              <ChartContainer config={chartConfig} className="h-[300px]">
                <AreaChart data={chartData}>
                  <CartesianGrid 
                    strokeDasharray="none" 
                    stroke="rgba(255,255,255,0.1)" 
                    horizontal={true}
                    vertical={true}
                  />
                  <XAxis 
                    dataKey="timestamp" 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: 'var(--muted-foreground)' }}
                  />
                  <YAxis 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: 'var(--muted-foreground)' }}
                    tickFormatter={(value) => `${value} Mbps`}
                  />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Legend 
                    verticalAlign="bottom" 
                    height={36}
                    iconType="rect"
                    wrapperStyle={{ 
                      paddingTop: '20px',
                      fontSize: '14px',
                      color: 'var(--muted-foreground)'
                    }}
                  />
                  {enabledMetrics.networkIn && (
                    <Area
                      type="monotone"
                      dataKey="networkIn"
                      stroke={chartConfig.networkIn.color}
                      fill={chartConfig.networkIn.color}
                      fillOpacity={0.3}
                      name="Network In"
                    />
                  )}
                  {enabledMetrics.networkOut && (
                    <Area
                      type="monotone"
                      dataKey="networkOut"
                      stroke={chartConfig.networkOut.color}
                      fill={chartConfig.networkOut.color}
                      fillOpacity={0.3}
                      name="Network Out"
                    />
                  )}
                </AreaChart>
              </ChartContainer>
            </CardContent>
          </Card>
        )}

        {/* CPU Performance Chart */}
        <Card>
          <CardHeader>
            <CardTitle>CPU Performance Details</CardTitle>
            <CardDescription>CPU queue length and context switches</CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-[300px]">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="none" stroke="rgba(255,255,255,0.1)" />
                <XAxis dataKey="timestamp" axisLine={false} tickLine={false} />
                <YAxis axisLine={false} tickLine={false} />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="cpuQueueLength"
                  stroke="#22c55e"
                  strokeWidth={2}
                  dot={false}
                  name="CPU Queue Length"
                />
                <Line
                  type="monotone"
                  dataKey="contextSwitches"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  dot={false}
                  name="Context Switches/sec"
                />
              </LineChart>
            </ChartContainer>
          </CardContent>
        </Card>

        {/* Memory Details Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Memory Usage Details</CardTitle>
            <CardDescription>Available, committed and page file usage</CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-[300px]">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="none" stroke="rgba(255,255,255,0.1)" />
                <XAxis dataKey="timestamp" axisLine={false} tickLine={false} />
                <YAxis axisLine={false} tickLine={false} tickFormatter={(value) => `${value}%`} />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="memoryAvailable"
                  stroke="#22c55e"
                  strokeWidth={2}
                  dot={false}
                  name="Available %"
                />
                <Line
                  type="monotone"
                  dataKey="memoryCommitted"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  dot={false}
                  name="Committed %"
                />
                <Line
                  type="monotone"
                  dataKey="pageFileUsage"
                  stroke="#ef4444"
                  strokeWidth={2}
                  dot={false}
                  name="Page File %"
                />
              </LineChart>
            </ChartContainer>
          </CardContent>
        </Card>

        {/* Disk Performance Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Disk Performance</CardTitle>
            <CardDescription>Read/Write latency and IOPS</CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-[300px]">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="none" stroke="rgba(255,255,255,0.1)" />
                <XAxis dataKey="timestamp" axisLine={false} tickLine={false} />
                <YAxis axisLine={false} tickLine={false} />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="diskReadLatency"
                  stroke="#22c55e"
                  strokeWidth={2}
                  dot={false}
                  name="Read Latency (ms)"
                />
                <Line
                  type="monotone"
                  dataKey="diskWriteLatency"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  dot={false}
                  name="Write Latency (ms)"
                />
                <Line
                  type="monotone"
                  dataKey="diskIOPS"
                  stroke="#8b5cf6"
                  strokeWidth={2}
                  dot={false}
                  name="IOPS"
                />
              </LineChart>
            </ChartContainer>
          </CardContent>
        </Card>

        {/* Network Performance Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Network Performance</CardTitle>
            <CardDescription>Packets per second and errors</CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-[300px]">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="none" stroke="rgba(255,255,255,0.1)" />
                <XAxis dataKey="timestamp" axisLine={false} tickLine={false} />
                <YAxis axisLine={false} tickLine={false} />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="networkPackets"
                  stroke="#22c55e"
                  strokeWidth={2}
                  dot={false}
                  name="Packets/sec"
                />
                <Line
                  type="monotone"
                  dataKey="networkErrors"
                  stroke="#ef4444"
                  strokeWidth={2}
                  dot={false}
                  name="Errors/sec"
                />
              </LineChart>
            </ChartContainer>
          </CardContent>
        </Card>

        {/* System Processes Chart */}
        <Card>
          <CardHeader>
            <CardTitle>System Activity</CardTitle>
            <CardDescription>Process and thread count over time</CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-[300px]">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="none" stroke="rgba(255,255,255,0.1)" />
                <XAxis dataKey="timestamp" axisLine={false} tickLine={false} />
                <YAxis axisLine={false} tickLine={false} />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="processCount"
                  stroke="#22c55e"
                  strokeWidth={2}
                  dot={false}
                  name="Processes"
                />
                <Line
                  type="monotone"
                  dataKey="threadCount"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  dot={false}
                  name="Threads"
                />
              </LineChart>
            </ChartContainer>
          </CardContent>
        </Card>

        {/* Ping Latency Chart */}
        {enabledMetrics.ping && (
          <Card>
            <CardHeader>
              <CardTitle>Ping Latency</CardTitle>
              <CardDescription>Network latency (ms)</CardDescription>
            </CardHeader>
            <CardContent>
              <ChartContainer config={chartConfig} className="h-[250px]">
                <LineChart data={chartData}>
                  <CartesianGrid 
                    strokeDasharray="none" 
                    stroke="rgba(255,255,255,0.1)" 
                    horizontal={true}
                    vertical={true}
                  />
                  <XAxis 
                    dataKey="timestamp" 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: 'var(--muted-foreground)' }}
                  />
                  <YAxis 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: 'var(--muted-foreground)' }}
                    tickFormatter={(value) => `${value}ms`}
                  />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Legend 
                    verticalAlign="bottom" 
                    height={36}
                    iconType="line"
                    wrapperStyle={{ 
                      paddingTop: '20px',
                      fontSize: '14px',
                      color: 'var(--muted-foreground)'
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="ping"
                    stroke={chartConfig.ping.color}
                    strokeWidth={2}
                    dot={false}
                    name="Ping Latency"
                  />
                </LineChart>
              </ChartContainer>
            </CardContent>
          </Card>
        )}
          </div>
        </TabsContent>

        {/* Processes & Services Tab */}
        <TabsContent value="processes" className="space-y-6">
          {/* Time Range Selector */}
          <div className="flex justify-end">
            <Select value={processesTimeRange} onValueChange={setProcessesTimeRange}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Select time range" />
              </SelectTrigger>
              <SelectContent>
                {timeRanges.map((range) => (
                  <SelectItem key={range.value} value={range.value}>
                    {range.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {latestMetrics && (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Processes</CardTitle>
                  <Activity className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{latestMetrics.process_count || 'N/A'}</div>
                  <p className="text-xs text-muted-foreground">Active processes</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Threads</CardTitle>
                  <Activity className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{latestMetrics.thread_count || 'N/A'}</div>
                  <p className="text-xs text-muted-foreground">Total threads</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Handles</CardTitle>
                  <Activity className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{serverInventory?.handles_count || latestMetrics.handles_count || 'N/A'}</div>
                  <p className="text-xs text-muted-foreground">Open handles</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Services Status</CardTitle>
                  <Settings className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {(() => {
                      const services = serverInventory?.windows_services 
                        ? parseJSON(serverInventory.windows_services, [])
                        : latestMetrics.windows_services 
                          ? parseJSON(latestMetrics.windows_services, [])
                          : [];
                      return Array.isArray(services) 
                        ? services.filter((s: any) => (s.Status === 4 || s.status === 'Running')).length 
                        : 'N/A';
                    })()}
                  </div>
                  <p className="text-xs text-muted-foreground">Running services</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Top Processes by CPU */}
          {latestMetrics?.top_processes_cpu && (
            <Card>
              <CardHeader>
                <CardTitle>Top Processes by CPU</CardTitle>
                <CardDescription>Processes consuming most CPU resources</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {toArray(latestMetrics.top_processes_cpu).slice(0, 10).map((process: any, index: number) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                      <div className="flex flex-col">
                        <span className="font-medium">{process.name}</span>
                        {process.pid && <span className="text-sm text-muted-foreground">PID: {process.pid}</span>}
                      </div>
                      <div className="text-right">
                        <div className="font-bold">{process.cpu}%</div>
                        <div className="text-sm text-muted-foreground">{process.mem_mb || process.memory}MB</div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Top Processes by Memory */}
          {latestMetrics?.top_processes_memory && (
            <Card>
              <CardHeader>
                <CardTitle>Top Processes by Memory</CardTitle>
                <CardDescription>Processes consuming most memory</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {toArray(latestMetrics.top_processes_memory).slice(0, 10).map((process: any, index: number) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                      <div className="flex flex-col">
                        <span className="font-medium">{process.name}</span>
                      </div>
                      <div className="text-right">
                        <div className="font-bold">{process.mem_mb} MB</div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Windows Services */}
          {latestMetrics?.windows_services && (
            <Card>
              <CardHeader>
                <CardTitle>Windows Services</CardTitle>
                <CardDescription>Service status overview</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {toArray(latestMetrics.windows_services).map((svc: any, index: number) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                      <div className="flex flex-col">
                        <span className="font-medium">{svc.display_name || svc.name}</span>
                        <span className="text-xs text-muted-foreground">{svc.name}</span>
                      </div>
                      <div className="text-right flex items-center gap-2">
                        <Badge variant={svc.status === 'Running' ? 'default' : 'destructive'}>
                          {svc.status}
                        </Badge>
                        <span className="text-xs text-muted-foreground">{svc.start_type}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security" className="space-y-6">
          {/* Time Range Selector */}
          <div className="flex justify-end">
            <Select value={securityTimeRange} onValueChange={setSecurityTimeRange}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Select time range" />
              </SelectTrigger>
              <SelectContent>
                {timeRanges.map((range) => (
                  <SelectItem key={range.value} value={range.value}>
                    {range.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {latestMetrics && (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Firewall</CardTitle>
                  <Shield className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {latestSecurityMetrics.firewall_enabled === true ? (
                      <Badge variant="default" className="bg-green-500">Enabled</Badge>
                    ) : latestSecurityMetrics.firewall_enabled === false ? (
                      <Badge variant="destructive">Disabled</Badge>
                    ) : 'N/A'}
                  </div>
                  <p className="text-xs text-muted-foreground">Windows Firewall</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Windows Defender</CardTitle>
                  <Shield className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {latestSecurityMetrics.windows_defender_enabled === true ? (
                      <Badge variant="default" className="bg-green-500">Active</Badge>
                    ) : latestSecurityMetrics.windows_defender_enabled === false ? (
                      <Badge variant="destructive">Inactive</Badge>
                    ) : 'N/A'}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {latestSecurityMetrics.windows_defender_updated === true ? 'Up to date' :
                     latestSecurityMetrics.windows_defender_updated === false ? 'Outdated' : 'Real-time protection'}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">TPM</CardTitle>
                  <Lock className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {latestSecurityMetrics.tpm_enabled === true ? (
                      <Badge variant="default" className="bg-green-500">Enabled</Badge>
                    ) : latestSecurityMetrics.tpm_enabled === false ? (
                      <Badge variant="secondary">Disabled</Badge>
                    ) : 'N/A'}
                  </div>
                  <p className="text-xs text-muted-foreground">Trusted Platform Module</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Secure Boot</CardTitle>
                  <Lock className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {latestSecurityMetrics.secure_boot_enabled === true ? (
                      <Badge variant="default" className="bg-green-500">Enabled</Badge>
                    ) : latestSecurityMetrics.secure_boot_enabled === false ? (
                      <Badge variant="secondary">Disabled</Badge>
                    ) : 'N/A'}
                  </div>
                  <p className="text-xs text-muted-foreground">UEFI Secure Boot</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Security Updates</CardTitle>
                  <AlertTriangle className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {latestSecurityMetrics.pending_security_updates != null ? (
                      <span className={latestSecurityMetrics.pending_security_updates > 0 ? 'text-red-500' : 'text-green-500'}>
                        {latestSecurityMetrics.pending_security_updates}
                      </span>
                    ) : 'N/A'}
                  </div>
                  <p className="text-xs text-muted-foreground">Pending critical updates</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Admin Accounts</CardTitle>
                  <Shield className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{latestSecurityMetrics.admin_accounts_count ?? 'N/A'}</div>
                  <p className="text-xs text-muted-foreground">Privileged accounts</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Failed Logins</CardTitle>
                  <AlertTriangle className="h-4 w-4 text-red-500" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {latestSecurityMetrics.failed_login_last_24h != null ? (
                      <span className={latestSecurityMetrics.failed_login_last_24h > 10 ? 'text-red-500' : ''}>
                        {latestSecurityMetrics.failed_login_last_24h}
                      </span>
                    ) : 'N/A'}
                  </div>
                  <p className="text-xs text-muted-foreground">Last 24 hours</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Antivirus</CardTitle>
                  <Shield className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-sm font-bold">{latestSecurityMetrics.antivirus_product ?? 'None detected'}</div>
                  <p className="text-xs text-muted-foreground">
                    {latestSecurityMetrics.antivirus_updated === true ? '✓ Updated' :
                     latestSecurityMetrics.antivirus_updated === false ? '⚠ Outdated' : 'Status unknown'}
                  </p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Privileged Users */}
          {latestMetrics?.privileged_users && (
            <Card>
              <CardHeader>
                <CardTitle>Privileged Users</CardTitle>
                <CardDescription>Members of Administrators group</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {toArray(latestMetrics.privileged_users)
                    .filter((user: any) => user.username && !user.username.startsWith('Alias name') && !user.username.startsWith('Comment'))
                    .map((user: any, index: number) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                      <div className="flex flex-col">
                        <span className="font-medium">{user.username || user.name}</span>
                        <span className="text-sm text-muted-foreground">{user.source || user.type || 'local'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Password Policy */}
          {latestMetrics?.password_policy && (
            <Card>
              <CardHeader>
                <CardTitle>Password Policy</CardTitle>
                <CardDescription>Current password requirements</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-3 md:grid-cols-2">
                  {(() => {
                    const policy: any = parseJSON(latestMetrics.password_policy, {});
                    return (
                      <>
                        <div className="flex justify-between p-2 bg-muted/50 rounded">
                          <span className="text-sm">Minimum Length:</span>
                          <span className="text-sm font-medium">{policy.min_length || 'N/A'} characters</span>
                        </div>
                        <div className="flex justify-between p-2 bg-muted/50 rounded">
                          <span className="text-sm">Max Password Age:</span>
                          <span className="text-sm font-medium">{policy.max_age_days || 'N/A'} days</span>
                        </div>
                        <div className="flex justify-between p-2 bg-muted/50 rounded">
                          <span className="text-sm">Min Password Age:</span>
                          <span className="text-sm font-medium">{policy.min_age_days || 'N/A'} days</span>
                        </div>
                        <div className="flex justify-between p-2 bg-muted/50 rounded">
                          <span className="text-sm">Password History:</span>
                          <span className="text-sm font-medium">{policy.history_length || 'N/A'} passwords</span>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Scheduled Tasks */}
          {latestMetrics?.scheduled_tasks && (
            <Card>
              <CardHeader>
                <CardTitle>Scheduled Tasks</CardTitle>
                <CardDescription>Active scheduled tasks</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {toArray(latestMetrics.scheduled_tasks).slice(0, 10).map((task: any, index: number) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                      <div className="flex flex-col">
                        <span className="font-medium">{task.name}</span>
                        <span className="text-xs text-muted-foreground">{task.path}</span>
                      </div>
                      <Badge variant={task.state === 'Running' || task.state === 'Ready' ? 'default' : 'secondary'}>
                        {task.state}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Startup Programs */}
          {latestMetrics?.startup_programs && (
            <Card>
              <CardHeader>
                <CardTitle>Startup Programs</CardTitle>
                <CardDescription>Programs that run at startup</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {toArray(latestMetrics.startup_programs).slice(0, 10).map((program: any, index: number) => (
                    <div key={index} className="flex flex-col p-2 bg-muted/50 rounded">
                      <span className="font-medium">{program.name}</span>
                      <span className="text-xs text-muted-foreground">{program.location}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Certificates Expiring */}
          {latestMetrics?.certificates_expiring && (
            <Card>
              <CardHeader>
                <CardTitle>Certificates Expiring Soon</CardTitle>
                <CardDescription>Certificates expiring within 30 days</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {toArray(latestMetrics.certificates_expiring).map((cert: any, index: number) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                      <div className="flex flex-col">
                        <span className="font-medium text-sm">{cert.subject}</span>
                        <span className="text-xs text-muted-foreground">Expires: {cert.expires}</span>
                      </div>
                      <Badge variant={cert.days_until_expiry < 7 ? 'destructive' : 'secondary'}>
                        {cert.days_until_expiry} days
                      </Badge>
                    </div>
                  ))}
                  {toArray(latestMetrics.certificates_expiring).length === 0 && (
                    <div className="text-sm text-muted-foreground">No certificates expiring soon</div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>


        {/* OS & Configuration Tab */}
        <TabsContent value="os" className="space-y-6">
          {/* Time Range Selector */}
          <div className="flex justify-end">
            <Select value={osTimeRange} onValueChange={setOsTimeRange}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Select time range" />
              </SelectTrigger>
              <SelectContent>
                {timeRanges.map((range) => (
                  <SelectItem key={range.value} value={range.value}>
                    {range.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {serverInventory && (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Pending Updates</CardTitle>
                  <Settings className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{serverInventory.pending_updates_count || '0'}</div>
                  <p className="text-xs text-muted-foreground">Windows updates</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Last Update</CardTitle>
                  <Clock className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-sm font-bold">
                    {serverInventory.last_update_time ? 
                      format(new Date(serverInventory.last_update_time), 'dd/MM/yyyy') : 'N/A'}
                  </div>
                  <p className="text-xs text-muted-foreground">Last update installed</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">BitLocker</CardTitle>
                  <Lock className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-sm font-bold">
                    {(() => {
                      const status = parseJSON(serverInventory.bitlocker_status, null);
                      if (!status || typeof status === 'string') return status || 'N/A';
                      return typeof status === 'object' ? Object.keys(status).length + ' volumes' : 'N/A';
                    })()}
                  </div>
                  <p className="text-xs text-muted-foreground">Protected volumes</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">License Status</CardTitle>
                  <Shield className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-sm font-bold">
                    <Badge variant={serverInventory.windows_license_status === 'Licensed' ? 'default' : 'destructive'}>
                      {serverInventory.windows_license_status || 'N/A'}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">Windows license</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* BitLocker Volumes Detail */}
          {latestMetrics?.bitlocker_status && (
            <Card>
              <CardHeader>
                <CardTitle>BitLocker Volumes</CardTitle>
                <CardDescription>Encryption status per volume</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {toArray(latestMetrics.bitlocker_status).map((vol: any, index: number) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-muted/50 rounded">
                      <div className="flex flex-col">
                        <span className="font-medium">{vol.mount_point}</span>
                        <span className="text-xs text-muted-foreground">{vol.encryption_method}</span>
                      </div>
                      <div className="text-right">
                        <Badge variant={vol.protection_status === 'On' ? 'default' : 'destructive'}>
                          {vol.protection_status}
                        </Badge>
                        <div className="text-xs text-muted-foreground mt-1">{vol.encryption_percentage}% encrypted</div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Disk Volumes */}
          {latestMetrics?.disk_volumes && (
            <Card>
              <CardHeader>
                <CardTitle>Disk Volumes</CardTitle>
                <CardDescription>All storage volumes</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {toArray(latestMetrics.disk_volumes).map((vol: any, index: number) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-muted/50 rounded">
                      <div className="flex flex-col">
                        <span className="font-medium">{vol.drive_letter}: {vol.label}</span>
                        <span className="text-xs text-muted-foreground">{vol.file_system} • {vol.size_gb} GB total</span>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-medium">{vol.free_gb} GB free</div>
                        <div className="text-xs text-muted-foreground">{vol.used_percent}% used</div>
                        <Badge variant={vol.health_status === 'Healthy' ? 'default' : 'destructive'} className="mt-1">
                          {vol.health_status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Backup Information */}
          {(latestMetrics?.last_backup_time || latestMetrics?.backup_status) && (
            <Card>
              <CardHeader>
                <CardTitle>Backup Status</CardTitle>
                <CardDescription>Windows Server Backup information</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Last Backup:</span>
                  <span className="text-sm font-medium">
                    {latestMetrics.last_backup_time ? 
                      format(new Date(latestMetrics.last_backup_time), 'dd/MM/yyyy HH:mm') : 'Never'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Status:</span>
                  <Badge variant={latestMetrics.backup_status === 'Success' ? 'default' : 'destructive'}>
                    {latestMetrics.backup_status || 'Unknown'}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Boot Time Info */}
          {latestMetrics?.boot_time_seconds && (
            <Card>
              <CardHeader>
                <CardTitle>Boot Performance</CardTitle>
                <CardDescription>System boot time</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Last Boot:</span>
                  <span className="text-sm font-medium">
                    {latestMetrics.last_boot_time ? 
                      format(new Date(latestMetrics.last_boot_time), 'dd/MM/yyyy HH:mm') : 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Boot Time:</span>
                  <span className="text-sm font-medium">{latestMetrics.boot_time_seconds} seconds</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Time Zone:</span>
                  <span className="text-sm font-medium">{latestMetrics.time_zone || 'N/A'}</span>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Event Logs Tab */}
        <TabsContent value="events" className="space-y-6">
          {/* Time Range Selector for Events */}
          <div className="flex justify-end">
            <Select value={eventsTimeRange} onValueChange={setEventsTimeRange}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Select time range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="15m">Last 15 minutes</SelectItem>
                <SelectItem value="1h">Last 1 hour</SelectItem>
                <SelectItem value="24h">Last 24 hours</SelectItem>
                <SelectItem value="7d">Last 7 days</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {latestMetrics && (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              <Card 
                className="cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => {
                  setSelectedEventType('login_success');
                  setEventDialogOpen(true);
                }}
              >
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Login Success</CardTitle>
                  <Shield className="h-4 w-4 text-green-500" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600">{eventTotals.login_success_count || 0}</div>
                  <p className="text-xs text-muted-foreground">Successful logins</p>
                  <p className="text-[11px] text-muted-foreground/80">Click to view raw events (may differ from agent totals if logs are missing/excluded)</p>
                  <p className="text-xs text-muted-foreground">Successful logins</p>
                </CardContent>
              </Card>

              <Card 
                className="cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => {
                  setSelectedEventType('login_failure');
                  setEventDialogOpen(true);
                }}
              >
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Login Failed</CardTitle>
                  <AlertTriangle className="h-4 w-4 text-red-500" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-red-600">{eventTotals.login_failed_count || 0}</div>
                  <p className="text-xs text-muted-foreground">Failed logins</p>
                </CardContent>
              </Card>

              <Card 
                className="cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => {
                  setSelectedEventType('account_locked');
                  setEventDialogOpen(true);
                }}
              >
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Account Locked</CardTitle>
                  <Lock className="h-4 w-4 text-red-500" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-red-600">{eventTotals.account_locked_count || 0}</div>
                  <p className="text-xs text-muted-foreground">Lockouts</p>
                </CardContent>
              </Card>

              <Card 
                className="cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => {
                  setSelectedEventType('service_crash');
                  setEventDialogOpen(true);
                }}
              >
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Service Crashes</CardTitle>
                  <AlertTriangle className="h-4 w-4 text-yellow-500" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-yellow-600">{eventTotals.service_crash_count || 0}</div>
                  <p className="text-xs text-muted-foreground">Service crashes</p>
                </CardContent>
              </Card>

              <Card 
                className="cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => {
                  setSelectedEventType('app_crash');
                  setEventDialogOpen(true);
                }}
              >
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">App Crashes</CardTitle>
                  <AlertTriangle className="h-4 w-4 text-orange-500" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-orange-600">{eventTotals.application_crash_count || 0}</div>
                  <p className="text-xs text-muted-foreground">App crashes</p>
                </CardContent>
              </Card>

              <Card 
                className="cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => {
                  setSelectedEventType('powershell');
                  setEventDialogOpen(true);
                }}
              >
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">PowerShell</CardTitle>
                  <Settings className="h-4 w-4 text-blue-500" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-blue-600">{eventTotals.powershell_execution_count || 0}</div>
                  <p className="text-xs text-muted-foreground">Script executions</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Event Details Dialog */}
          <EventDetailsDialog
            open={eventDialogOpen}
            onOpenChange={setEventDialogOpen}
            hostname={selectedServer}
            eventType={selectedEventType}
            eventTitle={
              selectedEventType === 'login_success' ? 'Login Success' :
              selectedEventType === 'login_failure' ? 'Login Failed' :
              selectedEventType === 'account_locked' ? 'Account Locked' :
              selectedEventType === 'service_crash' ? 'Service Crashes' :
              selectedEventType === 'app_crash' ? 'App Crashes' :
              selectedEventType === 'powershell' ? 'PowerShell Executions' :
              'Events'
            }
            timeRange={eventsTimeRange}
          />
        </TabsContent>

        {/* Network & Exposure Tab */}
        <TabsContent value="network" className="space-y-6">
          {/* Time Range Selector */}
          <div className="flex justify-end">
            <Select value={networkTimeRange} onValueChange={setNetworkTimeRange}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Select time range" />
              </SelectTrigger>
              <SelectContent>
                {timeRanges.map((range) => (
                  <SelectItem key={range.value} value={range.value}>
                    {range.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {latestMetrics && (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">RDP Status</CardTitle>
                  <Globe className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    <Badge variant={latestMetrics.rdp_enabled ? 'destructive' : 'default'}>
                      {latestMetrics.rdp_enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">Remote Desktop</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">WinRM Status</CardTitle>
                  <Settings className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    <Badge variant={latestMetrics.winrm_enabled ? 'destructive' : 'default'}>
                      {latestMetrics.winrm_enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">Windows Remote Management</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">SMBv1 Status</CardTitle>
                  <AlertTriangle className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    <Badge variant={latestMetrics.smbv1_enabled ? 'destructive' : 'default'}>
                      {latestMetrics.smbv1_enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">SMBv1 Protocol</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Listening Ports</CardTitle>
                  <Wifi className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  {(() => {
                    const invPorts = toPortsArray(serverInventory?.listening_ports);
                    let histPorts = toPortsArray(latestMetrics?.listening_ports);
                    if (histPorts.length === 0) {
                      for (let i = metricsData.length - 1; i >= 0; i--) {
                        const p = toPortsArray(metricsData[i]?.listening_ports);
                        if (p.length > 0) { histPorts = p; break; }
                      }
                    }
                    const ports = invPorts.length > 0 ? invPorts : histPorts;
                    return (
                      <>
                        <div className="text-2xl font-bold">{ports.length}</div>
                        <p className="text-xs text-muted-foreground">Open ports</p>
                      </>
                    );
                  })()}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Listening Ports List */}
          <Card>
              <CardHeader>
                <CardTitle>Listening Ports</CardTitle>
                <CardDescription>Currently open network ports</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
                  {(() => {
                    const invPorts = toPortsArray(serverInventory?.listening_ports);
                    const histPorts = toPortsArray(latestMetrics?.listening_ports);
                    const ports = invPorts.length > 0 ? invPorts : histPorts;
                    if (ports.length === 0) {
                      return (
                        <div className="text-sm text-muted-foreground col-span-full">No open ports reported in the selected time range.</div>
                      );
                    }
                    return ports.slice(0, 12).map((port: any, index: number) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                        <div className="flex flex-col">
                          <span className="font-medium">{port.port}/{port.protocol}</span>
                          <span className="text-sm text-muted-foreground">{port.process}</span>
                        </div>
                        <Badge variant={port.whitelisted ? 'default' : 'destructive'}>
                          {port.whitelisted ? 'Known' : 'New'}
                        </Badge>
                      </div>
                    ));
                  })()}
                </div>
              </CardContent>
            </Card>

          {/* Domain & DNS Info */}
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Domain Information</CardTitle>
                <CardDescription>Active Directory membership</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Domain Joined:</span>
                  <span className="text-sm font-medium">
                    {latestNetworkMetrics?.domain_joined === true ? (
                      <Badge variant="default">Yes</Badge>
                    ) : latestNetworkMetrics?.domain_joined === false ? (
                      <Badge variant="secondary">No</Badge>
                    ) : 'N/A'}
                  </span>
                </div>
                {latestNetworkMetrics?.domain_name && (
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Domain Name:</span>
                    <span className="text-sm font-medium">{latestNetworkMetrics.domain_name}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Default Gateway:</span>
                  <span className="text-sm font-medium">{latestNetworkMetrics?.default_gateway || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">NTP Server:</span>
                  <span className="text-sm font-medium">{latestNetworkMetrics?.ntp_server || 'N/A'}</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>DNS Servers</CardTitle>
                <CardDescription>Configured DNS servers</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-1">
                  {toArray(latestNetworkMetrics?.dns_servers).map((dns: string, index: number) => (
                    <div key={index} className="text-sm font-mono bg-muted/50 p-2 rounded">
                      {dns}
                    </div>
                  ))}
                  {toArray(latestNetworkMetrics?.dns_servers).length === 0 && (
                    <div className="text-sm text-muted-foreground">No DNS servers configured</div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Network Adapters */}
          {latestMetrics?.network_adapters && (
            <Card>
              <CardHeader>
                <CardTitle>Network Adapters</CardTitle>
                <CardDescription>Physical network interfaces</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {toArray(latestMetrics?.network_adapters).map((adapter: any, index: number) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-muted/50 rounded">
                      <div className="flex flex-col">
                        <span className="font-medium">{adapter.name}</span>
                        <span className="text-xs text-muted-foreground">{adapter.description}</span>
                        <span className="text-xs font-mono text-muted-foreground">MAC: {adapter.mac_address}</span>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-medium">{adapter.link_speed}</div>
                        <Badge variant="default" className="mt-1">{adapter.status}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Network Shares */}
          {latestMetrics?.network_shares && toArray(latestMetrics?.network_shares).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Network Shares</CardTitle>
                <CardDescription>SMB shares on this system</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {toArray(latestMetrics?.network_shares).map((share: any, index: number) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                      <div className="flex flex-col">
                        <span className="font-medium">{share.name}</span>
                        <span className="text-xs text-muted-foreground">{share.path}</span>
                        {share.description && (
                          <span className="text-xs text-muted-foreground">{share.description}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* TCP Connections Info */}
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>TCP Connections</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Established:</span>
                  <span className="text-sm font-medium">{latestMetrics?.tcp_established_connections || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Time Wait:</span>
                  <span className="text-sm font-medium">{latestMetrics?.tcp_time_wait_connections || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Open Sessions:</span>
                  <span className="text-sm font-medium">{latestMetrics?.open_sessions || 0}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Inventory & Asset Tab */}
        <TabsContent value="inventory" className="space-y-6">
          {/* Time Range Selector */}
          <div className="flex justify-end">
            <Select value={inventoryTimeRange} onValueChange={setInventoryTimeRange}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Select time range" />
              </SelectTrigger>
              <SelectContent>
                {timeRanges.map((range) => (
                  <SelectItem key={range.value} value={range.value}>
                    {range.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {serverInventory && (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Database className="h-5 w-5" />
                    Machine Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Serial:</span>
                    <span className="text-sm font-medium">{latestInventoryMetrics?.device_serial || serverInventory?.device_serial || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">BIOS:</span>
                    <span className="text-sm font-medium">{latestInventoryMetrics?.bios_version || serverInventory?.bios_version || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">OS Edition:</span>
                    <span className="text-sm font-medium">{latestInventoryMetrics?.os_edition || serverInventory?.os_edition || 'N/A'}</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Server className="h-5 w-5" />
                    Virtualization
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Type:</span>
                    <span className="text-sm font-medium">{latestInventoryMetrics?.vm_type || serverInventory?.vm_type || 'Physical'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Generation:</span>
                    <span className="text-sm font-medium">{latestInventoryMetrics?.vm_generation || serverInventory?.vm_generation || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Hypervisor:</span>
                    <span className="text-sm font-medium">
                      {(() => {
                        const info = parseJSON(latestInventoryMetrics?.hypervisor_info || serverInventory?.hypervisor_info, null);
                        if (!info) return 'N/A';
                        if (typeof info === 'string') return info;
                        return info?.type || info?.name || 'N/A';
                      })()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Time Zone:</span>
                    <span className="text-sm font-medium text-xs">{latestInventoryMetrics?.time_zone || serverInventory?.time_zone || 'N/A'}</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Info className="h-5 w-5" />
                    Software Summary
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Installed Apps:</span>
                    <span className="text-sm font-medium">
                      {(() => {
                        const software = parseJSON(serverInventory?.installed_software, []);
                        return Array.isArray(software) ? software.length : 'N/A';
                      })()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Server Roles:</span>
                    <span className="text-sm font-medium">
                      {(() => {
                        const roles = parseJSON(serverInventory?.installed_roles, []);
                        return Array.isArray(roles) ? roles.length : '0';
                      })()}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {loading && (
        <div className="text-center text-muted-foreground">
          Loading metrics data...
        </div>
      )}

      {!loading && chartData.length === 0 && selectedServer && (
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-muted-foreground">
              Geen metrics data beschikbaar voor {selectedServer} in de laatste {timeRanges.find(r => r.value === getCurrentTimeRange())?.label.toLowerCase()}.
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Zorg dat je monitoring script actief is op de server.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
